import React, { useEffect, useMemo, useRef, useState } from 'react';

import { useRouter } from 'next/router';

import { AdvancedBarGraph } from '@custom-components/AdvancedBarGraph';
import { getEnecoBeXapiSiteApiV1PricingDynamic } from '@dc-be/client/services.gen';
import { PriceAggregationDto } from '@dc-be/client/types.gen';
import usePublicDCBE from '@dc-be/hooks/usePublicDCBE';
import { DynamicPricingGraphRendering } from '@sitecore/types/DynamicPricingGraph';
import {
  Box,
  Button,
  Card,
  Grid,
  IconButton,
  InputSelect,
  PageGrid,
  Skeleton,
  Stack,
  Table,
  Text,
  TextLink,
  VisuallyHidden,
} from '@sparky';
import { ChevronRightIcon, ChevronLeftIcon } from '@sparky/icons';
import { InputWrapper, StyledInput } from '@sparky/util/../components/InputDatepicker/InputDatepicker.styles';

const hours = [
  '00:00',
  '01:00',
  '02:00',
  '03:00',
  '04:00',
  '05:00',
  '06:00',
  '07:00',
  '08:00',
  '09:00',
  '10:00',
  '11:00',
  '12:00',
  '13:00',
  '14:00',
  '15:00',
  '16:00',
  '17:00',
  '18:00',
  '19:00',
  '20:00',
  '21:00',
  '22:00',
  '23:00',
];

function formatDateWithDashes(date: Date) {
  return `${date.getFullYear()}-${(date.getMonth() + 1 + '').padStart(2, '0')}-${(date.getDate() + '').padStart(2, '0')}`;
}

function formatDateWithSlashes(date: Date) {
  return `${(date.getDate() + '').padStart(2, '0')}/${(date.getMonth() + 1 + '').padStart(2, '0')}/${date.getFullYear()}`;
}

function addDay(date: string, dayValue: number) {
  // Split the string into an array of components
  const dateParts = date.split('-');

  // Extract the year, month, and day
  const year = parseInt(dateParts[0], 10);
  const month = parseInt(dateParts[1], 10) - 1; // Convert to zero-based month
  const day = parseInt(dateParts[2], 10);

  return formatDateWithDashes(new Date(year, month, day + dayValue));
}

function addMonths(date: string, months: number) {
  const newDate = new Date(date);
  newDate.setMonth(newDate.getMonth() + months);
  return newDate;
}

const DynamicPricingGraph: React.FC<DynamicPricingGraphRendering> = ({
  fields: {
    title,
    showDataButtonText,
    todayLinkText,
    tomorrowLinkText,
    separatorText,
    dropdownByHourText,
    dropdownByDayText,
    dropdownByMonthText,
    xAxisNoDataLabel,
    graphAveragePriceTitle,
    yAxisLabel,
    xAxisEnergyLabel,
    xAxisAveragePriceLabel,
    previousText,
    nextText,
    priceTableByHourTitle,
    priceTableHourText,
    priceTableDateText,
    priceTablePriceText,
    noDataText,
    fromText,
    toText,
  },
}) => {
  const router = useRouter();

  const today = formatDateWithDashes(new Date());
  const tomorrow = addDay(today, 1);
  const threeMonthsAgo = formatDateWithDashes(addMonths(today, -3));
  const tomorrowAvailable = new Date().getHours() >= 13;

  const pageGridRef = useRef<HTMLDivElement>(null);

  const aggregations = [
    {
      label: dropdownByHourText.value,
      value: 'hourly',
    },
    {
      label: dropdownByDayText.value,
      value: 'daily',
    },
    {
      label: dropdownByMonthText.value,
      value: 'monthly',
    },
  ];

  const [startDate, setStartDate] = useState(`${router.query.startDate ?? today}`);
  const [endDate, setEndDate] = useState(`${router.query.endDate ?? today}`);
  const [aggregation, setAggregation] = useState(
    aggregations.find(agg => agg.value === router.query.aggregation)?.value ?? aggregations[0].value,
  );

  const [aggregationInput, setAggregationInput] = useState(aggregation);
  const [dateInput, setDateInput] = useState(startDate);

  const { isLoading, data } = usePublicDCBE(
    getEnecoBeXapiSiteApiV1PricingDynamic,
    {
      query: {
        startDate: startDate,
        endDate: endDate,
        aggregation: aggregation as PriceAggregationDto,
      },
    },
    [startDate, endDate, aggregation],
  );
  const records = useMemo(
    () =>
      data?.data?.data?.records?.map(rec => ({
        ...rec,
        date: formatDateWithSlashes(new Date(rec.date || '')),
        month: new Date(rec?.date || '').toLocaleString('default', { month: 'long' }),
      })),
    [data],
  );
  const days = useMemo(() => records?.map(rec => rec.date ?? '') ?? [], [data]);
  const months = useMemo(() => records?.map(rec => rec.month) ?? [], [data]);

  const [previousButtonActive, setPreviousButtonActive] = useState(true);
  const [nextButtonActive, setNextButtonActive] = useState(true);
  const [graphWidth, setGraphWidth] = useState(1200);

  useEffect(() => {
    const now = new Date();
    const firstMondayFull = new Date();
    const weekOffset = (now.getDay() === 6 && tomorrowAvailable) || now.getDay() === 0 ? 0 : 7;

    const threeMonthsAgoDate = new Date(threeMonthsAgo);
    const lastMondayFull = new Date(threeMonthsAgo);
    const lastWeekOffset = threeMonthsAgoDate.getDay() !== 1 ? 7 : 0;

    switch (aggregation) {
      case 'monthly':
        setPreviousButtonActive(false);
        setNextButtonActive(false);
        break;
      case 'daily':
        firstMondayFull.setDate(now.getDate() - ((now.getDay() + 6) % 7) - weekOffset);
        lastMondayFull.setDate(threeMonthsAgoDate.getDate() - ((threeMonthsAgoDate.getDay() + 6) % 7) + lastWeekOffset);

        setPreviousButtonActive(startDate !== formatDateWithDashes(lastMondayFull));
        setNextButtonActive(startDate !== formatDateWithDashes(firstMondayFull));
        break;
      case 'hourly':
      default:
        setPreviousButtonActive(
          new Date(new Date(threeMonthsAgo).setHours(23, 59, 59)).getTime() < new Date(startDate).getTime(),
        );
        setNextButtonActive(!((startDate === today && !tomorrowAvailable) || startDate === tomorrow));
        break;
    }
  }, [startDate, endDate, aggregation]);

  function handleResize() {
    let width = 1200;

    if (pageGridRef.current) {
      switch (aggregation) {
        case 'hourly':
          width = 1200;
          break;
        case 'daily':
          width = Math.max(650, pageGridRef.current.offsetWidth - 48);
          break;
        case 'monthly':
          width = Math.max(250, pageGridRef.current.offsetWidth - 48);
          break;
        default:
          width = 1200;
      }
    }

    setGraphWidth(width);
  }

  useEffect(() => {
    window.addEventListener('resize', handleResize);
    handleResize();

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [aggregation]);

  const averagePrice = useMemo(() => {
    const sum =
      records
        ?.map(item => item.price ?? 0)
        .reduce((prev, next) => {
          return prev + next;
        }, 0) ?? 0;

    return sum / (records?.length ?? 0);
  }, [records]);

  const graphHeight = useMemo(() => {
    const prices = records?.map(r => r.price || 0);
    const minPrice = prices !== undefined ? Math.min(...prices) : 0;
    const maxPrice = prices !== undefined ? Math.max(...prices) : 80;
    const minY = minPrice < 0 ? Math.floor(minPrice * 0.11) * 10 : 0;
    const maxY = Math.round(maxPrice * 0.11) * 10;

    return { minY, maxY };
  }, [records]);

  const updateData = () => {
    let newStartDate;
    let newEndDate;

    const now = new Date();
    const weekOffset = (now.getDay() === 6 && tomorrowAvailable) || now.getDay() === 0 ? 0 : 7;
    const firstMondayFull = new Date();

    switch (aggregationInput) {
      case 'monthly':
        newStartDate = formatDateWithDashes(addMonths(threeMonthsAgo, 1));
        newEndDate = today;
        break;
      case 'daily':
        firstMondayFull.setDate(now.getDate() - ((now.getDay() + 6) % 7) - weekOffset);

        newStartDate = formatDateWithDashes(firstMondayFull);
        newEndDate = addDay(formatDateWithDashes(firstMondayFull), 6);
        break;
      case 'hourly':
      default:
        newStartDate = newEndDate = dateInput;
        break;
    }

    setStartDate(newStartDate);
    setEndDate(newEndDate);
    setAggregation(aggregationInput);
  };

  const todayLink = () => {
    setStartDate(today);
    setEndDate(today);
    setAggregation('hourly');
  };
  const tomorrowLink = () => {
    setStartDate(tomorrow);
    setEndDate(tomorrow);
    setAggregation('hourly');
  };
  const previousPeriod = () => {
    let newStartDate;
    let newEndDate;

    switch (aggregation) {
      case 'daily':
        newStartDate = addDay(startDate, -7);
        newEndDate = addDay(endDate, -7);
        break;
      case 'hourly':
      default:
        newStartDate = newEndDate = addDay(startDate, -1);
        break;
    }

    setStartDate(newStartDate);
    setEndDate(newEndDate);
  };
  const nextPeriod = () => {
    let newStartDate;
    let newEndDate;

    switch (aggregation) {
      case 'daily':
        newStartDate = addDay(startDate, 7);
        newEndDate = addDay(endDate, 7);
        break;
      case 'hourly':
      default:
        newStartDate = newEndDate = addDay(startDate, 1);
        break;
    }

    setStartDate(newStartDate);
    setEndDate(newEndDate);
  };

  return (
    <PageGrid>
      <Grid.Item gridColumn={{ initial: '1/-1' }}>
        <Box paddingY={6}>
          <Stack gap={6}>
            <h2>{title.value}</h2>
            <Stack direction={'row'} gap={4} alignY={'center'} wrap>
              <TextLink emphasis={startDate === today ? 'medium' : 'low'} onClick={() => todayLink()}>
                {todayLinkText.value}
              </TextLink>
              {tomorrowAvailable && (
                <TextLink emphasis={startDate === tomorrow ? 'medium' : 'low'} onClick={() => tomorrowLink()}>
                  {tomorrowLinkText.value}
                </TextLink>
              )}

              <Box>
                <strong>{separatorText.value}</strong>
              </Box>

              <Stack gap={4} alignY={'center'} direction={'row'} wrap>
                <InputSelect
                  filteredElement="datepicker"
                  name="aggregationInput"
                  type="filter"
                  visuallyHideLabel={true}
                  label="Aggregation"
                  defaultValue={aggregations[0].value}
                  options={aggregations}
                  onChange={e => setAggregationInput(e.target.value)}
                />

                {aggregationInput === aggregations[0].value && (
                  <InputWrapper>
                    <Stack direction="row" gap="2">
                      <Stack.Item grow>
                        <VisuallyHidden>
                          <label htmlFor="dateInput">{priceTableDateText.value}</label>
                        </VisuallyHidden>

                        <StyledInput
                          id="dateInput"
                          name="dateInput"
                          type="date"
                          defaultValue={startDate}
                          min={threeMonthsAgo}
                          max={tomorrowAvailable ? tomorrow : today}
                          onChange={e => setDateInput(e.target.value)}
                          autoComplete="off"
                        />
                      </Stack.Item>
                    </Stack>
                  </InputWrapper>
                )}

                <Button size="compact" onClick={() => updateData()}>
                  {showDataButtonText.value}
                </Button>
              </Stack>
            </Stack>
            <Box paddingTop={2}>
              <Card corners={'rounded'}>
                <Box paddingY={4} paddingX={4}>
                  <Stack gap={2} alignY={'center'}>
                    <Stack gap={4} direction={'row'} alignX={'center'} alignY={'center'}>
                      {previousButtonActive && (
                        <IconButton label={previousText.value} onClick={() => previousPeriod()}>
                          <ChevronLeftIcon />
                        </IconButton>
                      )}
                      <Stack alignX={'center'}>
                        <Text size="BodyL" weight="bold">
                          {aggregation === aggregations[2].value
                            ? `${fromText.value} ${records?.[0]?.month ?? '...'} ${toText.value} ${records?.[records.length - 1]?.month ?? '...'}`
                            : aggregation === aggregations[1].value
                              ? `${fromText.value} ${formatDateWithSlashes(new Date(startDate))} ${toText.value} ${formatDateWithSlashes(new Date(endDate))}`
                              : formatDateWithSlashes(new Date(startDate))}
                        </Text>
                        {!isNaN(averagePrice) && (
                          <Text size="BodyXS">
                            {graphAveragePriceTitle.value} €{averagePrice.toFixed(2)}
                          </Text>
                        )}
                      </Stack>
                      {nextButtonActive && (
                        <IconButton label={nextText.value} onClick={() => nextPeriod()}>
                          <ChevronRightIcon />
                        </IconButton>
                      )}
                    </Stack>
                    <Box overflow={'auto'} paddingBottom={4} ref={pageGridRef}>
                      {isLoading && <Skeleton variant="rectangular" height={641} />}
                      {!isLoading && records && (
                        <AdvancedBarGraph
                          width={graphWidth}
                          height={600}
                          records={records}
                          xAxis={{
                            category:
                              aggregation === aggregations[2].value
                                ? 'month'
                                : aggregation === aggregations[1].value
                                  ? 'date'
                                  : 'time',
                            data:
                              aggregation === aggregations[2].value
                                ? months
                                : aggregation === aggregations[1].value
                                  ? days
                                  : hours,
                          }}
                          yAxis={{
                            category: 'price',
                            label: yAxisLabel.value,
                            min: graphHeight.minY,
                            max: graphHeight.maxY,
                          }}
                          average={averagePrice}
                          legend={[
                            { label: xAxisEnergyLabel.value, color: '#7EC389' },
                            { label: xAxisNoDataLabel.value, color: '#8C8B8D' },
                          ]}
                          noConnection={!records?.length}
                          noConnectionLabel={noDataText.value}
                          averagePriceLabel={xAxisAveragePriceLabel.value}
                        />
                      )}
                    </Box>
                  </Stack>
                </Box>
              </Card>
            </Box>

            {aggregation === aggregations[0].value && (
              <>
                <h2>{priceTableByHourTitle.value}</h2>
                <Table>
                  <Table.Header>
                    <span>{priceTableDateText.value}</span>
                    <span>{priceTableHourText.value}</span>
                    <span>{priceTablePriceText.value}</span>
                  </Table.Header>
                  {records?.map((item, key) => (
                    <Table.Row key={(item.time ?? '') + key}>
                      <span>{item.date}</span>
                      <span>{item.time}</span>
                      <span>€ {item.price?.toFixed(2)}</span>
                    </Table.Row>
                  ))}
                </Table>
              </>
            )}
          </Stack>
        </Box>
      </Grid.Item>
    </PageGrid>
  );
};

export default DynamicPricingGraph;
